import { AssignmentTypeTag } from "components/AssignmentTypeTag";
import { ActivityType } from "enums/ActivityType.enum";
import { Uppdrag, UppdragStatus } from "types/uppdrag.type";
import { getSeparatorDetailedLabel } from "types/separator/separator.helpers";
import { InspectionStatusTag } from "components/InspectionStatusTag";
import { useCreateUppgiftLogic } from "./useCreateUppgiftLogic";
import { CreateUppgiftModal } from "./CreateUppgiftModal";
import { WasteModal } from "../transportSedel/WasteModal";
import { useState } from "react";
import EmptyState from "components/EmptyState";
import { SeparatorIcon } from "components/icons/SeparatorIcon";
import { Link } from "components/Link";
import { Separator } from "types/separator/separator.type";
import { InspectionStatus } from "enums/InspectionStatus.enum";
import { TransportSedelStatus } from "types/transportSedel.type";
import { TransportSedelStatusTag } from "components/TransportSedelStatusTag";
import { UppdragPageTab } from "../UppdragPage";
import { LinkProps } from "@tanstack/react-router";
import { useCreateTransportSedelLogic } from "../transportSedel/useCreateTransportSedelLogic";
import { TransportSedelModal } from "../transportSedel/TransportSedelModal";
import { useCreateWasteLogic } from "../transportSedel/useCreateWasteLogic";
import BasicTable from "components/basicTable/BasicTable";
import { Notes } from "components/Notes";
import { Headline4 } from "components/typography/Headline4";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import SecondaryButton from "components/buttons/SecondaryButton";
import { WaterMeasurementStatusTag } from "components/WaterMeasurementStatusTag";
import { WaterMeasurementProtocolStatus } from "types/waterMeasurementProtocol/waterMeasurementProtocol.helpers";
import { DotMenu } from "components/DotMenu";
import DangerModal from "components/DangerModal";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNotification } from "hooks/useNotification";
import { deleteSixMonthsInspection } from "api/sixMonthsInspection.api";
import { deleteFiveYearsInspection } from "api/fiveYearsInspection.api";
import { deleteWaterMeasurementProtocol } from "api/waterMeasurementProtocol.api";
import {
  deleteTransportSedel,
  deleteTransportSedelWaste,
  TransportSedelQueryKey,
} from "api/transportSedel.api";
import { UppdragQueryKey } from "api/uppdrag.api";

type Props = {
  uppdrag: Uppdrag;
};

type UppgiftDeleteTarget = {
  type: ActivityType;
  id: number;
  /** Only for Tomning: the waste's parent transportsedel */
  transportSedelId?: number;
};

export const UppgiftTab = ({ uppdrag }: Props) => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  // Delete uppgift (one modal, endpoint dispatched per activity type)
  const [uppgiftToDelete, setUppgiftToDelete] =
    useState<UppgiftDeleteTarget | null>(null);

  const deleteUppgiftMutation = useMutation({
    mutationFn: async (target: UppgiftDeleteTarget) => {
      switch (target.type) {
        case ActivityType.SixMonthsInspection:
          return deleteSixMonthsInspection().mutationFn(target.id);
        case ActivityType.FiveYearInspection:
          return deleteFiveYearsInspection().mutationFn(target.id);
        case ActivityType.WaterMeasurementProtocol:
          return deleteWaterMeasurementProtocol().mutationFn(target.id);
        case ActivityType.Tomning: {
          // If this is the last waste on the transportsedel, delete the
          // whole transportsedel instead of leaving an empty one behind
          const wasteCountOnSedel = uppdrag.wasteList.filter(
            (w) => w.transportSedel.id === target.transportSedelId,
          ).length;
          if (wasteCountOnSedel <= 1) {
            return deleteTransportSedel().mutationFn(target.transportSedelId!);
          }
          return deleteTransportSedelWaste().mutationFn({
            transportSedelId: target.transportSedelId!,
            wasteId: target.id,
          });
        }
        default:
          throw new Error(`Unknown uppgift type: ${target.type}`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [UppdragQueryKey.GetOne] });
      queryClient.invalidateQueries({
        queryKey: [TransportSedelQueryKey.GetList],
      });
      setUppgiftToDelete(null);
    },
    onError: () => notification.error(),
  });

  // Add Tömning
  // Detta används för att hålla koll på vilken avskiljare man valde i första steget, för att sedan autovälja detta i WasteModal
  const [tomningSeparatorUuid, setTomningSeparatorUuid] = useState<
    string | undefined
  >();
  const transportSedelId =
    uppdrag.transportSedelList.find((x) => true)?.id ?? -1;

  const createWasteLogic = useCreateWasteLogic({
    transportSedel: { id: transportSedelId },
    uppdrag,
    separatorUuid: tomningSeparatorUuid,
    setTomningSeparatorUuid,
    isSeparatorDropdownHidden: false,
    isSetSeparatorDisabled: true,
  });

  const addTransportSedelLogic = useCreateTransportSedelLogic(
    uppdrag,
    createWasteLogic.openModal,
  );

  // Uppgift
  const createUppgiftLogic = useCreateUppgiftLogic({
    uppdrag,
    openWasteModal: createWasteLogic.openModal,
    openTransportSedelModal: addTransportSedelLogic.openModal,
    setTomningSeparatorUuid,
  });

  const itemCount =
    uppdrag.sixMonthsInspectionList.length +
    uppdrag.fiveYearsInspectionList.length +
    uppdrag.wasteList.length +
    uppdrag.waterMeasurementProtocolList.length;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <Headline4 className="flex items-center text-lg sm:text-base">
          Aktiviteter
          {uppdrag.areAllUppgiftsCompleted && !!uppdrag.uppgiftCount && (
            <CheckCircleIcon className="ml-0.5 h-5 w-5 text-cyan-400" />
          )}
        </Headline4>
        {itemCount > 0 && uppdrag.status !== UppdragStatus.Completed && (
          <SecondaryButton size="small" onClick={createUppgiftLogic.openModal}>
            + Ny aktivitet
          </SecondaryButton>
        )}
      </div>
      <CreateUppgiftModal logic={createUppgiftLogic} />
      <TransportSedelModal
        title="Skapa transportsedel"
        logic={addTransportSedelLogic}
        actionText="Nästa"
      />
      <WasteModal
        title="Ny avfallstransport"
        logic={createWasteLogic}
        hideSeparatorDropdownIfNoSeparatorPreSelected
      />
      <DangerModal
        title="Ta bort aktivitet"
        isOpen={!!uppgiftToDelete}
        onClose={() => setUppgiftToDelete(null)}
        isLoading={deleteUppgiftMutation.isPending}
        loadingText="Tar bort..."
        actionHandler={() => {
          if (uppgiftToDelete) {
            deleteUppgiftMutation.mutate(uppgiftToDelete);
          }
        }}
      />
      {itemCount === 0 ? (
        <EmptyState
          onClick={createUppgiftLogic.openModal}
          header="Inga aktiviteter"
          subHeader="Lägg till din första aktivitet för att komma igång"
          buttonText="Ny aktivitet"
          iconType="uppgift"
        />
      ) : (
        <>
          {/* Mobile */}
          <div className="flex flex-col pt-4 sm:hidden">
            {uppdrag.sixMonthsInspectionList.map((x) => (
              <UppgiftItemMobile
                key={`6mån-${x.id}`}
                separator={{
                  ...x.separator,
                  facility: uppdrag.facility,
                }}
                type={ActivityType.SixMonthsInspection}
                uppgiftLink={{
                  to: "/app/uppdrag/$uuid/sixm/$sixmid",
                  params: { uuid: uppdrag.uuid, sixmid: x.id.toString() },
                }}
                inspectionStatus={x.status}
                onDelete={() =>
                  setUppgiftToDelete({
                    type: ActivityType.SixMonthsInspection,
                    id: x.id,
                  })
                }
              />
            ))}
            {uppdrag.fiveYearsInspectionList.map((x) => (
              <UppgiftItemMobile
                key={`5år-${x.id}`}
                separator={{
                  ...x.separator,
                  facility: uppdrag.facility,
                }}
                type={ActivityType.FiveYearInspection}
                uppgiftLink={{
                  to: "/app/uppdrag/$uuid/fivey/$fiveyid",
                  params: { uuid: uppdrag.uuid, fiveyid: x.id.toString() },
                }}
                inspectionStatus={x.status}
                onDelete={() =>
                  setUppgiftToDelete({
                    type: ActivityType.FiveYearInspection,
                    id: x.id,
                  })
                }
              />
            ))}
            {uppdrag.wasteList
              // .filter((x) => !!x.tomning)
              .map((x) => (
                <UppgiftItemMobile
                  key={`töm-${x.id}`}
                  separator={
                    !x.tomning?.separator
                      ? undefined
                      : {
                          ...x.tomning.separator,
                          facility: uppdrag.facility,
                        }
                  }
                  type={ActivityType.Tomning}
                  uppgiftLink={{
                    to: "/app/uppdrag/$uuid",
                    params: { uuid: uppdrag.uuid },
                    search: { tab: UppdragPageTab.TransportSedel },
                  }}
                  transportSedelStatus={x.transportSedel.status}
                  onDelete={() =>
                    setUppgiftToDelete({
                      type: ActivityType.Tomning,
                      id: x.id,
                      transportSedelId: x.transportSedel.id,
                    })
                  }
                />
              ))}
            {uppdrag.waterMeasurementProtocolList.map((x) => (
              <UppgiftItemMobile
                key={`prov-${x.id}`}
                separator={{
                  ...x.separator,
                  facility: uppdrag.facility,
                }}
                type={ActivityType.WaterMeasurementProtocol}
                uppgiftLink={{
                  to: "/app/uppdrag/$uuid/provtagning/$pid",
                  params: { uuid: uppdrag.uuid, pid: x.id.toString() },
                }}
                waterMeasurementStatus={x.status}
                onDelete={() =>
                  setUppgiftToDelete({
                    type: ActivityType.WaterMeasurementProtocol,
                    id: x.id,
                  })
                }
              />
            ))}
          </div>
          {/* Desktop */}
          <BasicTable className="hidden sm:block">
            {/* <BasicTable.THead>
              <BasicTable.TR>
                <BasicTable.TH>Typ</BasicTable.TH>
                <BasicTable.TH>Objekt</BasicTable.TH>
                <BasicTable.TH>Status</BasicTable.TH>
                <BasicTable.TH></BasicTable.TH>
              </BasicTable.TR>
            </BasicTable.THead> */}
            <BasicTable.TBody>
              {uppdrag.sixMonthsInspectionList.map((x) => (
                <UppgiftItemDesktop
                  key={`6mån-${x.id}`}
                  separator={{
                    ...x.separator,
                    facility: uppdrag.facility,
                  }}
                  type={ActivityType.SixMonthsInspection}
                  uppgiftLink={{
                    to: "/app/uppdrag/$uuid/sixm/$sixmid",
                    params: { uuid: uppdrag.uuid, sixmid: x.id.toString() },
                  }}
                  inspectionStatus={x.status}
                  onDelete={() =>
                    setUppgiftToDelete({
                      type: ActivityType.SixMonthsInspection,
                      id: x.id,
                    })
                  }
                />
              ))}
              {uppdrag.fiveYearsInspectionList.map((x) => (
                <UppgiftItemDesktop
                  key={`5år-${x.id}`}
                  separator={{
                    ...x.separator,
                    facility: uppdrag.facility,
                  }}
                  type={ActivityType.FiveYearInspection}
                  uppgiftLink={{
                    to: "/app/uppdrag/$uuid/fivey/$fiveyid",
                    params: { uuid: uppdrag.uuid, fiveyid: x.id.toString() },
                  }}
                  inspectionStatus={x.status}
                  onDelete={() =>
                    setUppgiftToDelete({
                      type: ActivityType.FiveYearInspection,
                      id: x.id,
                    })
                  }
                />
              ))}
              {uppdrag.wasteList
                // .filter((x) => !!x.tomning)
                .map((x) => (
                  <UppgiftItemDesktop
                    key={`töm-${x.id}`}
                    separator={
                      !x.tomning?.separator
                        ? undefined
                        : {
                            ...x.tomning.separator,
                            facility: uppdrag.facility,
                          }
                    }
                    type={ActivityType.Tomning}
                    uppgiftLink={{
                      to: "/app/uppdrag/$uuid",
                      params: { uuid: uppdrag.uuid },
                      search: { tab: UppdragPageTab.TransportSedel },
                    }}
                    transportSedelStatus={x.transportSedel.status}
                    onDelete={() =>
                      setUppgiftToDelete({
                        type: ActivityType.Tomning,
                        id: x.id,
                        transportSedelId: x.transportSedel.id,
                      })
                    }
                  />
                ))}
              {uppdrag.waterMeasurementProtocolList.map((x) => (
                <UppgiftItemDesktop
                  key={`prov-${x.id}`}
                  separator={{
                    ...x.separator,
                    facility: uppdrag.facility,
                  }}
                  type={ActivityType.WaterMeasurementProtocol}
                  uppgiftLink={{
                    to: "/app/uppdrag/$uuid/provtagning/$pid",
                    params: { uuid: uppdrag.uuid, pid: x.id.toString() },
                  }}
                  waterMeasurementStatus={x.status}
                  onDelete={() =>
                    setUppgiftToDelete({
                      type: ActivityType.WaterMeasurementProtocol,
                      id: x.id,
                    })
                  }
                />
              ))}
            </BasicTable.TBody>
          </BasicTable>
        </>
      )}
    </div>
  );
};

type UppgiftItemProps = {
  type: ActivityType;
  separator?: Separator;
  uppgiftLink: Pick<LinkProps, "to" | "params" | "search">;
  inspectionStatus?: InspectionStatus;
  transportSedelStatus?: TransportSedelStatus;
  waterMeasurementStatus?: WaterMeasurementProtocolStatus;
  onDelete: () => void;
};

const UppgiftItemMobile = ({
  type,
  separator,
  uppgiftLink,
  inspectionStatus,
  transportSedelStatus,
  waterMeasurementStatus,
  onDelete,
}: UppgiftItemProps) => (
  <div className="mb-4 flex flex-col rounded-md border border-gray-100 p-4 shadow-md sm:hidden">
    <div className="mb-3 flex items-center justify-between">
      <AssignmentTypeTag type={type} />
      {!!inspectionStatus && <InspectionStatusTag status={inspectionStatus} />}
      {!!transportSedelStatus && (
        <TransportSedelStatusTag status={transportSedelStatus} />
      )}
      {!!waterMeasurementStatus && (
        <WaterMeasurementStatusTag status={waterMeasurementStatus} />
      )}
    </div>
    {!!separator && (
      <div className="flex max-w-[300px] flex-col space-y-2">
        <span className="flex items-center truncate">
          <SeparatorIcon className="mr-1" />{" "}
          <Link
            to="/app/kunder/$customerId/facility/$facilityId/separator/$separatorId"
            params={{
              customerId: separator.facility.customer.id.toString(),
              facilityId: separator.facility.id.toString(),
              separatorId: separator.id.toString(),
            }}
            variant="gray"
          >
            {getSeparatorDetailedLabel(separator)}
          </Link>
        </span>
        {separator.notes ? <Notes text={separator.notes} /> : null}
      </div>
    )}

    <div className="mt-5 flex items-center justify-end gap-4">
      <Link {...uppgiftLink} isLinkButton>
        Öppna
      </Link>
      <DotMenu
        items={[{ label: "Ta bort", onClick: onDelete, isDelete: true }]}
      />
    </div>
  </div>
);

const UppgiftItemDesktop = ({
  type,
  separator,
  uppgiftLink,
  inspectionStatus,
  transportSedelStatus,
  waterMeasurementStatus,
  onDelete,
}: UppgiftItemProps) => (
  <BasicTable.TR>
    <BasicTable.TD>
      <AssignmentTypeTag type={type} />
    </BasicTable.TD>
    <BasicTable.TD className="flex max-w-[320px] flex-col space-y-2 text-sm xl:max-w-none">
      {!!separator ? (
        <>
          <span className="flex items-center">
            <SeparatorIcon className="mr-1" />
            <Link
              to="/app/kunder/$customerId/facility/$facilityId/separator/$separatorId"
              params={{
                customerId: separator.facility.customer.id.toString(),
                facilityId: separator.facility.id.toString(),
                separatorId: separator.id.toString(),
              }}
              className="overflow-hidden text-ellipsis"
              variant="gray"
            >
              {getSeparatorDetailedLabel(separator)}
            </Link>
          </span>
          {separator.notes ? <Notes text={separator.notes} /> : null}
        </>
      ) : (
        <></>
      )}
    </BasicTable.TD>

    <BasicTable.TD>
      {!!inspectionStatus && <InspectionStatusTag status={inspectionStatus} />}
      {!!transportSedelStatus && (
        <TransportSedelStatusTag status={transportSedelStatus} />
      )}
      {!!waterMeasurementStatus && (
        <WaterMeasurementStatusTag status={waterMeasurementStatus} />
      )}
    </BasicTable.TD>
    <BasicTable.TD className="text-right">
      <div className="flex items-center justify-end gap-4">
        <Link {...uppgiftLink} isLinkButton>
          Öppna
        </Link>
        <DotMenu
          items={[{ label: "Ta bort", onClick: onDelete, isDelete: true }]}
        />
      </div>
    </BasicTable.TD>
  </BasicTable.TR>
);
